import { ITaggedTelemetryPropertyType, TelemetryEventPropertyType } from '@fluidframework/common-definitions';
import { makeBooleanDataField, makeDoubleDataField, makeStringDataField } from '@microsoft/oteljs';

// Inspect the tag, if any, and use that to interpret how the property can/should be logged
// Alternative implementation could leverage the DataClassification enum in oteljs and leave behavior to that package
function handlePropertyTag(
  taggableProp: TelemetryEventPropertyType | ITaggedTelemetryPropertyType
): TelemetryEventPropertyType {
  const { value, tag } =
    taggableProp !== null && typeof taggableProp === 'object' ? taggableProp : { value: taggableProp, tag: undefined };
  // See TelemetryDataTag enum in @fluidframework/telemetry-utils for known values
  switch (tag) {
    case undefined:
      // No tag means we can log plainly
      return value;
    case 'PackageData':
    case 'CodeArtifact':
      // We don't load 3P code in 1P apps so data about code artifacts like package name is ok for now.
      // If these could be 3P, depending on Privacy Agreement and whether the package is public or private,
      // this could be considered customer content and would need to be handled as such.
      return value;
    case 'UserData':
      // Strip out anything tagged explicitly as user personal data.
      // Alternate strategy would be to hash these props.
      return 'REDACTED (tag: UserData)';
    default:
      // If we encounter a tag we don't recognize then we should scrub to be safe.
      return `REDACTED (tag: ${tag})`;
  }
}

// Helper method that returns Data fields in the form that Otel expects
export const getOtelDataField = (
  taggableProp: TelemetryEventPropertyType | ITaggedTelemetryPropertyType,
  key: string
) => {
  const value = handlePropertyTag(taggableProp);
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === 'string') return makeStringDataField(key, value);
  if (typeof value === 'number') return makeDoubleDataField(key, value);
  return makeBooleanDataField(key, value);
};
